// @flow strict
import * as React from 'react'
import {
  LegacyFooter,
  SEOHead,
  type PropsWithChildren,
  CookieAlert,
  MainNavigation,
  BusinessNavigation,
  type Location,
  USNavigation,
  USFooter,
  BusinessFooter,
  PlusFooter,
  PremiumFooter,
  PaidNavigation,
} from '@mondough/public-web-components'
import './Global.module.scss'
import { type PageTheme } from './contentful/contentSectionsRenderer'
type GlobalProps = {
  pageMetadata: React.Element<typeof SEOHead>,
  location: Location,
  theme: PageTheme,
  className?: string,
}

export const locale = 'en-GB'
export const mobileBreakpointNumber = 768
export const smallBase64Image =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

const Global = ({
  children,
  pageMetadata,
  location,
  className,
  theme,
}: PropsWithChildren<GlobalProps>) => {
  const queryParams = new URLSearchParams(location.search)
  const shouldHideNavigation: boolean =
    queryParams.get('hideNavigation') === 'true' ? true : false
  const shouldHideFooter: boolean =
    queryParams.get('hideFooter') === 'true' ? true : false

  return (
    // Browsers seem to have different ways to display location so cleaning up the string just in case
    <div className={className}>
      {pageMetadata}
      <CookieAlert />
      {!shouldHideNavigation && <Nav theme={theme} />}
      <main>{children}</main>
      {!shouldHideFooter && <Footer theme={theme} location={location} />}
    </div>
  )
}

export default Global

const Nav = ({ theme }: { theme: PageTheme }) => {
  // This will override the default monzo.com header with a custom component
  // Add to this list when you want to change the header of a page
  const customHeaderMap: { [k: PageTheme]: React.Node } = {
    default: <MainNavigation />,
    paid: <PaidNavigation />,
    business: <BusinessNavigation />,
    usa: <USNavigation />,
  }

  return customHeaderMap[theme]
}

const Footer = ({
  theme,
  location,
  ...otherProps
}: {
  theme: PageTheme,
  location: Location,
}) => {
  // This will override the default monzo.com footer with a custom component
  // Add to this list when you want to change the footer of a page
  const customFooterMap: { [k: PageTheme]: React.Node } = {
    default: <LegacyFooter location={location} {...otherProps} />,
    paid:
      location && location.pathname.match(/^\/i\/monzo-premium\/*/) ? (
        <PremiumFooter {...otherProps} />
      ) : (
        <PlusFooter {...otherProps} />
      ),
    business: <BusinessFooter {...otherProps} />,
    usa: <USFooter {...otherProps} />,
  }

  return customFooterMap[theme]
}
